import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import apiClient from '../utils/api';
import { setPartnersCache, setDepartmentsCache, syncExportedArrays } from '../utils/analytics';

export interface Partner {
    id: string;
    name: string;
    partnerType: string;
    isAttendanceCheckEnabled: boolean;
}

interface PartnersContextType {
    partners: Partner[];
    departments: Partner[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

const PartnersContext = createContext<PartnersContextType | undefined>(undefined);

export function PartnersProvider({ children }: { children: ReactNode }) {
    const [partners, setPartners] = useState<Partner[]>([]);
    const [departments, setDepartments] = useState<Partner[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        // Only fetch if user is authenticated
        const token = localStorage.getItem('token');
        const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

        if (!token || !isAuthenticated) {
            // User not authenticated, set empty arrays and skip loading
            setPartners([]);
            setDepartments([]);
            setLoading(false);
            setError(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const [partnersData, departmentsData] = await Promise.all([
                apiClient.getPartners(),
                apiClient.getDepartments(),
            ]);
            setPartners(partnersData);
            setDepartments(departmentsData);
            // Populate cache in analytics.ts
            setPartnersCache(partnersData);
            setDepartmentsCache(departmentsData);
            // Sync exported arrays for backward compatibility
            syncExportedArrays();
        } catch (err) {
            console.error('Failed to fetch partners/departments:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch partners and departments');
            // Set empty arrays on error to prevent crashes
            setPartners([]);
            setDepartments([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();

        // Listen for authentication changes
        const handleStorageChange = () => {
            fetchData();
        };

        window.addEventListener('storage', handleStorageChange);

        // Also listen for custom auth events
        window.addEventListener('auth-changed', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('auth-changed', handleStorageChange);
        };
    }, []);

    return (
        <PartnersContext.Provider value={{ partners, departments, loading, error, refresh: fetchData }}>
            {children}
        </PartnersContext.Provider>
    );
}

export function usePartners() {
    const context = useContext(PartnersContext);
    if (context === undefined) {
        throw new Error('usePartners must be used within a PartnersProvider');
    }
    return context;
}

